// src/pages/SurpriseReveal.tsx
import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCameraRetro,
  FaCheckCircle,
  FaCompass,
  FaEnvelopeOpenText,
  FaEye,
  FaGift,
  FaHeart,
  FaLock,
  FaMagic,
  FaMapMarkedAlt,
  FaShoppingBag,
  FaStar,
  FaTicketAlt,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import ExternalForfeoRedirectModal from '../components/ExternalForfeoRedirectModal';
import { FORFEO_GIFT_CARD_URL } from '../lib/forfeoExternalLinks';

type RevealTheme = {
  id: string;
  icon: React.ReactNode;
  titleFr: string;
  titleEn: string;
  clueFr: string[];
  clueEn: string[];
  revealFr: string;
  revealEn: string;
  gradient: string;
  accent: string;
  ctaPath: string;
};

const REVEAL_THEMES: RevealTheme[] = [
  {
    id: 'romantic',
    icon: <FaHeart />,
    titleFr: 'Moment romantique',
    titleEn: 'Romantic moment',
    clueFr: ['Une ambiance douce', 'Un lieu à découvrir à deux', 'Un moment qui reste en mémoire'],
    clueEn: ['A soft atmosphere', 'A place to discover together', 'A moment worth remembering'],
    revealFr: 'Une escapade locale pensée pour créer un vrai souvenir à deux.',
    revealEn: 'A local escape designed to create a real memory for two.',
    gradient: 'from-rose-500 via-pink-500 to-fuchsia-600',
    accent: 'text-pink-600 bg-pink-50 border-pink-100',
    ctaPath: '/offrir-un-moment?style=couple',
  },
  {
    id: 'family',
    icon: <FaCameraRetro />,
    titleFr: 'Moment famille',
    titleEn: 'Family moment',
    clueFr: ['Des sourires spontanés', 'Une sortie simple à organiser', 'Une expérience à raconter après'],
    clueEn: ['Spontaneous smiles', 'An easy outing to plan', 'An experience to talk about later'],
    revealFr: 'Une activité locale pour rassembler la famille autour d’une belle découverte.',
    revealEn: 'A local activity to bring the family together around a great discovery.',
    gradient: 'from-amber-400 via-orange-500 to-red-500',
    accent: 'text-orange-600 bg-orange-50 border-orange-100',
    ctaPath: '/calendrier-des-occasions?filter=famille',
  },
  {
    id: 'quebec',
    icon: <FaMapMarkedAlt />,
    titleFr: 'Découverte du Québec',
    titleEn: 'Quebec discovery',
    clueFr: ['Une région à explorer', 'Une adresse locale', 'Une façon différente de voyager'],
    clueEn: ['A region to explore', 'A local address', 'A different way to travel'],
    revealFr: 'Un parcours pour découvrir le Québec autrement, entre expériences locales et adresses inspirantes.',
    revealEn: 'A journey to discover Quebec differently, through local experiences and inspiring places.',
    gradient: 'from-emerald-500 via-cyan-500 to-blue-600',
    accent: 'text-emerald-600 bg-emerald-50 border-emerald-100',
    ctaPath: '/decouvrir-quebec',
  },
  {
    id: 'last-minute',
    icon: <FaStar />,
    titleFr: 'Surprise de dernière minute',
    titleEn: 'Last-minute surprise',
    clueFr: ['Aucune panique', 'Un cadeau rapide', 'Un effet wow malgré tout'],
    clueEn: ['No panic', 'A quick gift', 'A wow effect anyway'],
    revealFr: 'Une carte Forfeo ou une expérience locale prête à offrir, même quand le temps presse.',
    revealEn: 'A Forfeo card or local experience ready to gift, even when time is short.',
    gradient: 'from-violet-500 via-indigo-500 to-blue-600',
    accent: 'text-violet-600 bg-violet-50 border-violet-100',
    ctaPath: '/carte-cadeau',
  },
];

const normalizeCode = (value: string) => value.trim().replace(/\s+/g, '').toUpperCase();

export default function SurpriseReveal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const isEn = String(i18n.language || '').toLowerCase().startsWith('en');

  const params = new URLSearchParams(location.search);
  const initialTheme = params.get('theme') || params.get('style') || 'quebec';
  const initialCode = params.get('code') || params.get('voucher') || '';

  const [selectedThemeId, setSelectedThemeId] = useState(
    REVEAL_THEMES.some((theme) => theme.id === initialTheme) ? initialTheme : 'quebec'
  );
  const [step, setStep] = useState(0);
  const [voucherCode, setVoucherCode] = useState(initialCode);
  const [isForfeoComRedirectOpen, setIsForfeoComRedirectOpen] = useState(false);

  const selectedTheme = useMemo(
    () => REVEAL_THEMES.find((theme) => theme.id === selectedThemeId) || REVEAL_THEMES[0],
    [selectedThemeId]
  );

  const clues = isEn ? selectedTheme.clueEn : selectedTheme.clueFr;
  const revealTitle = isEn ? selectedTheme.titleEn : selectedTheme.titleFr;
  const revealText = isEn ? selectedTheme.revealEn : selectedTheme.revealFr;
  const cleanCode = normalizeCode(voucherCode);
  const hasForfeoCode = cleanCode.startsWith('FE-') || cleanCode.startsWith('FE');

  const handleNextRevealStep = () => {
    setStep((current) => Math.min(current + 1, clues.length));
  };

  const handleRestart = () => setStep(0);

  const c = {
    badge: isEn ? 'Surprise reveal' : 'Révélation surprise',
    title: isEn ? 'Turn a gift into a reveal moment' : 'Transformez un cadeau en moment de révélation',
    intro: isEn
      ? 'Create a small wow effect before the recipient discovers their Forfeo moment. Perfect after buying a virtual card on forfeo.com or choosing an experience on Forfeo Explore.'
      : 'Créez un petit effet wow avant que la personne découvre son moment Forfeo. Idéal après l’achat d’une carte virtuelle sur forfeo.com ou le choix d’une expérience sur Forfeo Explore.',
    start: isEn ? 'Start the reveal' : 'Lancer la révélation',
    nextClue: isEn ? 'Show next clue' : 'Afficher l’indice suivant',
    revealNow: isEn ? 'Reveal the moment' : 'Révéler le moment',
    restart: isEn ? 'Restart' : 'Recommencer',
    buyCard: isEn ? 'Buy a Forfeo card' : 'Acheter une carte Forfeo',
    chooseMoment: isEn ? 'Choose a moment' : 'Choisir un moment',
    viewPurchases: isEn ? 'View my purchases' : 'Voir mes achats',
    clue: isEn ? 'Clue' : 'Indice',
    revealed: isEn ? 'Revealed' : 'Révélé',
    codeLabel: isEn ? 'Already have a Forfeo code?' : 'Vous avez déjà un code Forfeo ?',
    codePlaceholder: isEn ? 'Enter a code, example FE-123ABC' : 'Entrez un code, exemple FE-123ABC',
    codeHelp: isEn
      ? 'If this is a voucher from Forfeo Explore, your official ticket remains available in My purchases.'
      : 'Si c’est un voucher Forfeo Explore, le billet officiel reste disponible dans Mes achats.',
    codeDetected: isEn ? 'Forfeo code detected' : 'Code Forfeo détecté',
    bridgeTitle: isEn ? 'Two connected Forfeo worlds' : 'Deux univers Forfeo connectés',
    bridgeText: isEn
      ? 'Forfeo Explore inspires the gift. Forfeo.com is the official transactional site to buy the virtual Forfeo card.'
      : 'Forfeo Explore inspire le cadeau. Forfeo.com est le site transactionnel officiel pour acheter la carte virtuelle Forfeo.',
    howTitle: isEn ? 'How it works' : 'Comment ça fonctionne',
    step1: isEn ? 'Choose the kind of reveal you want to create.' : 'Choisissez le style de révélation à créer.',
    step2: isEn ? 'Send or show the animation to the recipient.' : 'Envoyez ou montrez l’animation à la personne.',
    step3: isEn ? 'The real purchase, card or ticket stays secure in the right Forfeo space.' : 'Le vrai achat, la carte ou le billet reste sécurisé dans le bon espace Forfeo.',
    secure: isEn ? 'This page does not replace the official voucher or payment receipt.' : 'Cette page ne remplace pas le voucher officiel ni le reçu de paiement.',
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden">
      <Header />
      <SeoHead
        title={isEn ? 'Surprise Reveal | Forfeo Explore' : 'Révélation surprise | Forfeo Explore'}
        description={isEn ? 'Create a premium reveal moment before gifting a Forfeo card or local experience.' : 'Créez un moment de révélation premium avant d’offrir une carte Forfeo ou une expérience locale.'}
        canonicalPath="/revelation-surprise"
      />

      <ExternalForfeoRedirectModal
        isOpen={isForfeoComRedirectOpen}
        onClose={() => setIsForfeoComRedirectOpen(false)}
        targetUrl={FORFEO_GIFT_CARD_URL}
        contextLabel="forfeo.com/carte-cadeau"
      />

      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0">
          <div className="absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-fuchsia-500/25 blur-[130px]" />
          <div className="absolute top-40 right-10 h-[420px] w-[420px] rounded-full bg-cyan-500/20 blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:38px_38px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.24em] text-cyan-100 backdrop-blur-xl mb-6">
              <FaMagic className="text-fuchsia-300" /> {c.badge}
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.88] mb-6">
              {c.title.split(' ').slice(0, 3).join(' ')}{' '}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-fuchsia-300 to-amber-200">
                {c.title.split(' ').slice(3).join(' ')}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl mb-8">{c.intro}</p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleNextRevealStep}
                className="inline-flex items-center justify-center gap-3 rounded-full bg-white text-slate-950 px-7 py-4 font-black uppercase tracking-widest text-xs shadow-2xl hover:-translate-y-1 transition"
              >
                <FaEye /> {step >= clues.length ? c.restart : c.start}
              </button>
              <button
                onClick={() => setIsForfeoComRedirectOpen(true)}
                className="inline-flex items-center justify-center gap-3 rounded-full border border-white/15 bg-white/10 px-7 py-4 font-black uppercase tracking-widest text-xs text-white hover:bg-white/15 transition"
              >
                <FaGift /> {c.buyCard}
              </button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }} className="relative">
            <div className={`absolute inset-0 bg-gradient-to-br ${selectedTheme.gradient} rounded-[3rem] blur-3xl opacity-40`} />
            <div className="relative rounded-[3rem] border border-white/15 bg-white/10 backdrop-blur-2xl p-5 md:p-7 shadow-2xl overflow-hidden">
              <div className={`rounded-[2.5rem] bg-gradient-to-br ${selectedTheme.gradient} p-[1px]`}>
                <div className="rounded-[2.5rem] bg-slate-950/90 p-7 md:p-9 min-h-[440px] flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-4 mb-8">
                      <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center text-3xl text-white">
                        {selectedTheme.icon}
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400 font-black">Forfeo</div>
                        <div className="font-black text-cyan-200">Explore</div>
                      </div>
                    </div>

                    <AnimatePresence mode="wait">
                      {step < clues.length ? (
                        <motion.div
                          key={`clue-${step}`}
                          initial={{ opacity: 0, y: 18, filter: 'blur(8px)' }}
                          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, y: -18, filter: 'blur(8px)' }}
                          transition={{ duration: 0.45 }}
                        >
                          <div className="text-[11px] font-black uppercase tracking-[0.24em] text-fuchsia-200 mb-3">
                            {c.clue} {Math.min(step + 1, clues.length)} / {clues.length}
                          </div>
                          <h2 className="text-3xl md:text-4xl font-black tracking-tight leading-tight mb-5">
                            {clues[Math.min(step, clues.length - 1)]}
                          </h2>
                          <p className="text-slate-400 leading-relaxed">
                            {isEn
                              ? 'The reveal is intentionally gradual: the gift becomes a small experience before the real moment begins.'
                              : 'La révélation est volontairement progressive : le cadeau devient une petite expérience avant même que le vrai moment commence.'}
                          </p>
                        </motion.div>
                      ) : (
                        <motion.div
                          key="revealed"
                          initial={{ opacity: 0, scale: 0.92, filter: 'blur(10px)' }}
                          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.55 }}
                        >
                          <div className="text-[11px] font-black uppercase tracking-[0.24em] text-emerald-200 mb-3">
                            <FaCheckCircle className="inline mr-2" /> {c.revealed}
                          </div>
                          <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mb-5">{revealTitle}</h2>
                          <p className="text-slate-300 text-lg leading-relaxed">{revealText}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    {step < clues.length ? (
                      <button
                        onClick={handleNextRevealStep}
                        className="flex-1 inline-flex items-center justify-center gap-3 rounded-2xl bg-white text-slate-950 px-5 py-4 font-black uppercase tracking-widest text-[11px] hover:bg-cyan-50 transition"
                      >
                        {step === clues.length - 1 ? c.revealNow : c.nextClue} <FaArrowRight />
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate(selectedTheme.ctaPath)}
                        className="flex-1 inline-flex items-center justify-center gap-3 rounded-2xl bg-white text-slate-950 px-5 py-4 font-black uppercase tracking-widest text-[11px] hover:bg-cyan-50 transition"
                      >
                        {c.chooseMoment} <FaArrowRight />
                      </button>
                    )}
                    <button
                      onClick={handleRestart}
                      className="inline-flex items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 font-black uppercase tracking-widest text-[11px] text-white hover:bg-white/10 transition"
                    >
                      {c.restart}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 bg-slate-50 text-slate-950 py-20 px-4">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
          <div className="rounded-[2.5rem] bg-white border border-slate-200 p-7 md:p-9 shadow-xl sticky top-24">
            <div className="w-14 h-14 rounded-2xl bg-violet-100 text-violet-600 flex items-center justify-center text-2xl mb-5">
              <FaTicketAlt />
            </div>
            <h2 className="text-3xl font-black tracking-tight mb-3">{c.codeLabel}</h2>
            <p className="text-slate-500 leading-relaxed mb-5">{c.codeHelp}</p>
            <input
              value={voucherCode}
              onChange={(event) => setVoucherCode(event.target.value)}
              placeholder={c.codePlaceholder}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 font-bold outline-none focus:ring-4 focus:ring-violet-100 focus:border-violet-400 transition"
            />
            {hasForfeoCode && (
              <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-800 p-4 text-sm font-bold flex items-center gap-3">
                <FaCheckCircle /> {c.codeDetected} : {cleanCode}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              <button
                onClick={() => navigate('/mes-achats')}
                className="rounded-2xl bg-slate-950 text-white px-5 py-4 font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-2 hover:bg-slate-800 transition"
              >
                <FaShoppingBag /> {c.viewPurchases}
              </button>
              <button
                onClick={() => navigate('/offrir-un-moment')}
                className="rounded-2xl bg-white border border-slate-200 text-slate-900 px-5 py-4 font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-2 hover:bg-slate-50 transition"
              >
                <FaGift /> {c.chooseMoment}
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-[2.5rem] bg-slate-950 text-white p-8 md:p-10 shadow-2xl overflow-hidden relative">
              <div className="absolute -right-20 -top-20 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-100 mb-5">
                  <FaLock /> {c.bridgeTitle}
                </div>
                <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">{c.bridgeTitle}</h2>
                <p className="text-slate-300 leading-relaxed max-w-2xl">{c.bridgeText}</p>
                <div className="grid md:grid-cols-2 gap-4 mt-8">
                  <button
                    onClick={() => setIsForfeoComRedirectOpen(true)}
                    className="rounded-3xl bg-white text-slate-950 p-6 text-left hover:-translate-y-1 transition shadow-xl"
                  >
                    <FaGift className="text-pink-500 text-2xl mb-4" />
                    <div className="font-black text-lg">forfeo.com</div>
                    <div className="text-sm text-slate-500 mt-1">{isEn ? 'Buy the virtual Forfeo card.' : 'Acheter la carte virtuelle Forfeo.'}</div>
                  </button>
                  <button
                    onClick={() => navigate('/decouvrir-quebec')}
                    className="rounded-3xl bg-white/10 border border-white/10 text-white p-6 text-left hover:bg-white/15 transition"
                  >
                    <FaCompass className="text-cyan-300 text-2xl mb-4" />
                    <div className="font-black text-lg">Forfeo Explore</div>
                    <div className="text-sm text-slate-300 mt-1">{isEn ? 'Discover experiences and ideas.' : 'Découvrir des expériences et idées.'}</div>
                  </button>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {[c.step1, c.step2, c.step3].map((item, index) => (
                <div key={item} className="rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
                  <div className="w-11 h-11 rounded-2xl bg-slate-100 text-slate-900 flex items-center justify-center font-black mb-4">{index + 1}</div>
                  <p className="text-sm font-bold text-slate-600 leading-relaxed">{item}</p>
                </div>
              ))}
            </div>

            <div className="rounded-3xl border border-amber-200 bg-amber-50 text-amber-900 p-6 flex gap-4">
              <FaEnvelopeOpenText className="text-2xl mt-1 shrink-0" />
              <p className="text-sm leading-relaxed font-semibold">{c.secure}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white text-slate-950 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight">{isEn ? 'Choose your reveal style' : 'Choisir le style de révélation'}</h2>
              <p className="text-slate-500 mt-2">{isEn ? 'Each style changes the clues and the final reveal.' : 'Chaque style change les indices et la révélation finale.'}</p>
            </div>
            <button
              onClick={() => navigate('/calendrier-des-occasions')}
              className="rounded-full bg-slate-950 text-white px-6 py-3 font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-2"
            >
              <FaCalendarAlt /> {isEn ? 'Occasion calendar' : 'Calendrier des occasions'}
            </button>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {REVEAL_THEMES.map((theme) => (
              <button
                key={theme.id}
                onClick={() => {
                  setSelectedThemeId(theme.id);
                  setStep(0);
                }}
                className={`text-left rounded-[2rem] border p-6 transition hover:-translate-y-1 ${selectedThemeId === theme.id ? theme.accent : 'bg-white border-slate-200 hover:bg-slate-50'}`}
              >
                <div className="text-3xl mb-5">{theme.icon}</div>
                <div className="font-black text-lg">{isEn ? theme.titleEn : theme.titleFr}</div>
                <div className="text-sm text-slate-500 mt-2 leading-relaxed">{isEn ? theme.clueEn[0] : theme.clueFr[0]}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
